import { Physics } from "@esotericsoftware/spine-webgl";
import type { SpinePreviewApp } from "../../../../lib/SpinePreviewApp";
import {
  DEFAULT_VAP_ENCODE_LIMITS,
  VAP_ORIGINAL_SPEC,
  VAP_PREVIEW_SPEC,
  normalizeVapEncodeLimits,
  vapFrameCount,
  type VapEncodeLimits,
  type VapDurationLimits,
} from "./vapConfig";

/** 与 Android VapEncoder / SpineRecorder 一致 */
export const VAP_EXPORT_ANIMATION = "idle_default";

/** 纯静态层无 Spine 动画时的 fallback 时长（秒） */
const STATIC_FALLBACK_SEC = 2;

/** 采帧前强制 idle_default（缺失则 fallback 第一个动画），并重置到 t=0 */
export function prepareAppsForVapExport(
  ...apps: (SpinePreviewApp | null | undefined)[]
): void {
  for (const app of apps) {
    if (!app) continue;
    for (const inst of app.instances) {
      const anim =
        inst.skeleton.data.findAnimation(VAP_EXPORT_ANIMATION)?.name ??
        inst.skeleton.data.animations[0]?.name;
      if (!anim) continue;
      inst.state.setAnimation(0, anim, true);
      const track = inst.state.getCurrent(0);
      if (track) track.trackTime = 0;
      inst.state.apply(inst.skeleton);
      inst.skeleton.updateWorldTransform(Physics.reset);
    }
  }
}

/** 从 skeleton 元数据读取 idle_default（或 fallback）时长 */
export function getMaxAnimationDurationSec(
  apps: (SpinePreviewApp | null | undefined)[],
): number {
  let maxSec = 0;
  for (const app of apps) {
    if (!app) continue;
    for (const inst of app.instances) {
      const anim =
        inst.skeleton.data.findAnimation(VAP_EXPORT_ANIMATION) ??
        inst.skeleton.data.animations[0];
      if (anim) {
        maxSec = Math.max(maxSec, anim.duration);
      }
    }
  }
  return maxSec;
}

/**
 * 原图：实际时长 = min(动画时长, maxDurationSec)
 */
export function getVapOriginalDurationMs(
  apps: (SpinePreviewApp | null | undefined)[],
  maxDurationSec: number = DEFAULT_VAP_ENCODE_LIMITS.originalMaxDurationSec,
  fps: number = VAP_ORIGINAL_SPEC.fps,
): number {
  const rawSec = getMaxAnimationDurationSec(apps);
  const baseSec = rawSec > 0 ? rawSec : STATIC_FALLBACK_SEC;
  const clampedSec = Math.min(baseSec, maxDurationSec);
  const frameCount = vapFrameCount(clampedSec, fps);
  return (frameCount / fps) * 1000;
}

/**
 * 缩略 preview：
 * nPrev = min(frameCount(max,fps), frameCount(min(raw,max),fps))
 * 纯静态层无动画时取满 maxDurationSec。
 */
export function getVapPreviewDurationMs(
  apps: (SpinePreviewApp | null | undefined)[],
  maxDurationSec: number = DEFAULT_VAP_ENCODE_LIMITS.previewMaxDurationSec,
  fps: number = VAP_PREVIEW_SPEC.fps,
): number {
  const rawSec = getMaxAnimationDurationSec(apps);
  const effectiveRaw = rawSec > 0 ? rawSec : maxDurationSec;
  const nPrev = Math.min(
    vapFrameCount(maxDurationSec, fps),
    vapFrameCount(Math.min(effectiveRaw, maxDurationSec), fps),
  );
  return (nPrev / fps) * 1000;
}

export type { VapEncodeLimits, VapDurationLimits };
export {
  normalizeVapEncodeLimits,
  DEFAULT_VAP_ENCODE_LIMITS,
  DEFAULT_VAP_DURATION_LIMITS,
} from "./vapConfig";
